import type { UiAsyncState } from '@/shared/ui/types'

export interface TableColumn<Row = Record<string, unknown>> {
  field: Extract<keyof Row, string>
  header: string
  /** Optional catalog-provided explanation shown while hovering the column header. */
  description?: string
  width?: number
  minWidth?: number
  maxWidth?: number
  /**
   * Skip a fixed pixel `width` for this column — under `table-layout: fixed`, a column with no
   * specified width absorbs whatever space the other, explicitly-sized columns don't claim,
   * instead of the browser dumping that leftover onto an arbitrary column (observed: the first).
   * `minWidth` still applies as a floor. At most one column per table should set this.
   */
  flexible?: boolean
  align?: 'start' | 'center' | 'end'
  sortable?: boolean
  frozen?: boolean
  numeric?: boolean
  resizable?: boolean
  onlyOnOverflow?: boolean
  /**
   * Let the cell wrap onto several lines instead of clipping to one with an ellipsis. For columns
   * whose values are prose (an AI-written label, a description) a fixed `width` plus `wrap` keeps
   * the column narrow and grows the row height, rather than the value stretching the table.
   */
  wrap?: boolean
  /**
   * A derived column that supports its neighbour rather than standing on its own (e.g. a report
   * metric's previous-period and change columns). Rendered on the sunken table surface so the
   * reader can tell it apart from the base metric at a glance.
   */
  supplementary?: boolean
}

export interface TableColumnGroup<Row = Record<string, unknown>> {
  key: string
  header: string
  fields: readonly Extract<keyof Row, string>[]
}

export type TableHeaderCell<Row extends Record<string, unknown>> =
  | { type: 'column'; column: TableColumn<Row>; columnIndex: number; rowspan?: number }
  | { type: 'group'; group: TableColumnGroup<Row>; columns: TableColumn<Row>[] }

export interface TableHeaderPlan<Row extends Record<string, unknown>> {
  columns: TableColumn<Row>[]
  rows: TableHeaderCell<Row>[][]
}

type TableHeaderSeed<Row extends Record<string, unknown>> =
  | { type: 'column'; column: TableColumn<Row> }
  | { type: 'group'; group: TableColumnGroup<Row>; columns: TableColumn<Row>[] }

export function resolveTableHeaderPlan<Row extends Record<string, unknown>>(
  source: TableColumn<Row>[],
  groups: readonly TableColumnGroup<Row>[] = [],
): TableHeaderPlan<Row> {
  const singleRow = (): TableHeaderPlan<Row> => ({
    columns: source,
    rows: [source.map((column, columnIndex) => ({ type: 'column', column, columnIndex }))],
  })
  if (groups.length === 0) return singleRow()

  const byField = new Map(source.map((column) => [column.field, column]))
  const owner = new Map<string, TableColumnGroup<Row>>()
  for (const group of groups) {
    for (const field of group.fields) {
      if (byField.has(field) && !owner.has(field)) owner.set(field, group)
    }
  }
  if (owner.size === 0) return singleRow()

  const grouped = new Map(
    groups.map((group) => [
      group,
      group.fields
        .filter((field, index, fields) => fields.indexOf(field) === index)
        .filter((field) => owner.get(field) === group)
        .map((field) => byField.get(field)!),
    ]),
  )
  const seeds: TableHeaderSeed<Row>[] = []
  const columns: TableColumn<Row>[] = []
  const seen = new Set<TableColumnGroup<Row>>()
  for (const column of source) {
    const group = owner.get(column.field)
    if (!group) {
      seeds.push({ type: 'column', column })
      columns.push(column)
    } else if (!seen.has(group)) {
      seen.add(group)
      const children = grouped.get(group) ?? []
      seeds.push({ type: 'group', group, columns: children })
      columns.push(...children)
    }
  }
  const indexByField = new Map(columns.map((column, index) => [column.field, index]))
  const top = seeds.map<TableHeaderCell<Row>>((seed) =>
    seed.type === 'group'
      ? seed
      : { ...seed, columnIndex: indexByField.get(seed.column.field)!, rowspan: 2 },
  )
  const bottom = top.flatMap<TableHeaderCell<Row>>((cell) =>
    cell.type === 'group'
      ? cell.columns.map((column) => ({
          type: 'column',
          column,
          columnIndex: indexByField.get(column.field)!,
        }))
      : [],
  )
  return { columns, rows: [top, bottom] }
}

export type SortState = { field: string; dir: 'asc' | 'desc' } | null
export type SortDirection = NonNullable<SortState>['dir']
export type TableDensity = 'default' | 'compact'
export type SortCycle = 'binary' | 'triState'

export interface DataTableProps<Row extends Record<string, unknown>> {
  rows: Row[]
  footerRows?: Row[]
  columns: TableColumn<Row>[]
  columnGroups?: readonly TableColumnGroup<Row>[]
  ariaLabel: string
  state?: UiAsyncState
  sort?: SortState
  draggable?: boolean
  freezeEnabled?: boolean
  expandable?: boolean
  frozenColumns?: Extract<keyof Row, string>[]
  rowKey?: (row: Row, index: number) => string | number
  getChildren?: (row: Row) => Row[]
  /** Row keys opened when a dataset is first rendered or its prescribed defaults change. */
  defaultExpandedKeys?: readonly (string | number)[]
  expandLabel?: (row: Row) => string
  collapseLabel?: (row: Row) => string
  resizeLabel?: (column: TableColumn<Row>) => string
  freezeLabel?: (column: TableColumn<Row>, frozen: boolean) => string
  /**
   * Fields for which the header shows a column-expand toggle (e.g. reveal adjacent
   * previous-value/change-vs-previous columns). Distinct from `expandable`, which controls
   * per-row breakdown expansion.
   */
  columnExpandableFields?: readonly Extract<keyof Row, string>[]
  columnExpandedFields?: readonly Extract<keyof Row, string>[]
  columnExpandLabel?: (column: TableColumn<Row>, expanded: boolean) => string
  onlyOnOverflow?: boolean
  skeletonRows?: number
  initialPageSize?: number
  pageSize?: number
  totalRows?: number
  loadMoreThreshold?: number
  /** Applies to numeric columns; v3 caps those at `max-w-40`. */
  defaultColumnWidth?: number
  /** Applies to every other column; v3 caps those at `max-w-56`. */
  defaultTextColumnWidth?: number
  clientSort?: boolean
  maxHeight?: string
  density?: TableDensity
  interactiveRows?: boolean
  sortCycle?: SortCycle
  initialSortDirection?: SortDirection
  /**
   * A background refetch is in flight while rows from the previous response are still on screen.
   * Rather than swap to the skeleton (which would discard the visible data), overlay it with a
   * dimmed backdrop and a spinner until the new response settles.
   */
  refreshing?: boolean
}
